import { defineStore } from 'pinia';
import { ref } from 'vue';
import { UserType, type Attendee } from '@/types';
import type { EntityAttendeeLabel } from '@/utils/api/api';

/** 参加者の表示名 */
export type AttendeeNameLabels = {
  primary: string | undefined;
  secondary: string | undefined;
  crew: string | undefined;
};

/**
 * Chime参加者のStore
 */
export const useAttendeeStore = defineStore('attendee', () => {
  /** 参加者 */
  const attendees = ref<Map<string, Attendee>>(new Map<string, Attendee>());

  /** 参加者の表示名 */
  const nameLabels = ref<AttendeeNameLabels>({
    primary: '',
    secondary: '',
    crew: ''
  });

  /**
   * 参加者を追加する
   * @param attendee
   */
  const addAttendee = (attendeeId: string, externalUserId: string, isContent: boolean): void => {
    console.debug(`[AttendeeStore] addAttendee ${attendeeId}, ${externalUserId}, ${isContent}`);

    const splitArray = externalUserId.split(':');

    const attendee: Attendee = {
      id: attendeeId,
      type: splitArray.length ? splitArray[0] : UserType.UNKNOWN,
      muted: false,
      signalStrength: 1,
      speaking: false,
      isContentAttendee: isContent
    };

    attendees.value.set(attendeeId, attendee);
  };

  /**
   * 参加者を削除する
   * @param id
   */
  const removeAttendee = (attendeeId: string): void => {
    console.debug(`[AttendeeStore] removeAttendee ${attendeeId}`);
    console.debug(attendees.value);

    attendees.value.delete(attendeeId);
  };

  /**
   * 参加者のミュート状態を更新する
   * @param attendeeId
   * @param muted
   */
  const updateMuteStatus = (attendeeId: string, muted: boolean) => {
    let attendee = attendees.value.get(attendeeId);
    if (attendee) {
      attendee.muted = muted;
      attendees.value.set(attendeeId, attendee);
    }
  };

  /**
   * 参加者の発言状態を更新する
   * @param attendeeId
   * @param speaking
   */
  const updateSpeakingStatus = (attendeeId: string, speaking: boolean) => {
    let attendee = attendees.value.get(attendeeId);
    if (attendee) {
      attendee.speaking = speaking;
      attendees.value.set(attendeeId, attendee);
    }
  };

  /**
   * 参加者のSignalStrengthを更新する
   * @param attendeeId
   * @param signalStrength
   */
  const updateSignalStrength = (attendeeId: string, signalStrength: number) => {
    let attendee = attendees.value.get(attendeeId);
    if (attendee) {
      attendee.signalStrength = signalStrength;
      attendees.value.set(attendeeId, attendee);
    }
  };

  /** 参加者の表示名を更新する */
  const refreshNameLabels = (labels: string) => {
    try {
      nameLabels.value = JSON.parse(labels);
    } catch (e: unknown) {
      console.warn('refreshNameLabels: ERROR');
      console.warn(e);
    }
  };

  /**
   * Storeの初期化
   */
  const clear = (): void => {
    attendees.value.clear();
    nameLabels.value = {
      primary: '',
      secondary: '',
      crew: ''
    };
  };

  return {
    /** 参加者 */
    attendees,
    nameLabels,
    addAttendee,
    removeAttendee,
    updateMuteStatus,
    updateSpeakingStatus,
    updateSignalStrength,
    refreshNameLabels,
    clear
  };
});
